import CheckIcon from "@mui/icons-material/Check";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import { IconButton, Tooltip, useTheme } from "@mui/material";
import React, { useEffect, useState } from "react";

export interface RichTextCopyButtonProps {
  /** HTML produced by the rich text editor. */
  html: string | null | undefined;
  /** Tooltip / aria label for the copy action. Default: "Copy" */
  copyLabel?: string;
}

const htmlToPlainText = (html: string): string => {
  const container = document.createElement("div");
  container.innerHTML = html;
  return (container.innerText || container.textContent || "").trim();
};

/**
 * Copies rich text to the clipboard as both HTML (keeps formatting when
 * pasted into an editor) and plain text. Falls back to plain text only.
 */
const RichTextCopyButton: React.FC<RichTextCopyButtonProps> = ({
  html,
  copyLabel = "Copy",
}) => {
  const theme = useTheme();
  const colors = theme.palette.customColors;
  const [copied, setCopied] = useState(false);
  const value = html || "";
  const text = value ? htmlToPlainText(value) : "";

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 1500);
    return () => clearTimeout(timer);
  }, [copied]);

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!text) return;
    try {
      if (typeof ClipboardItem !== "undefined" && navigator.clipboard.write) {
        await navigator.clipboard.write([
          new ClipboardItem({
            "text/html": new Blob([value], { type: "text/html" }),
            "text/plain": new Blob([text], { type: "text/plain" }),
          }),
        ]);
      } else {
        await navigator.clipboard.writeText(text);
      }
      setCopied(true);
    } catch {
      // ignore clipboard errors
    }
  };

  return (
    <Tooltip title={copied ? "Copied" : copyLabel}>
      <span>
        <IconButton
          size="small"
          onClick={(e) => {
            void handleCopy(e);
          }}
          disabled={!text}
          aria-label={copyLabel}
          sx={{
            p: 0.3,
            color: copied ? colors.purpleMain : colors.greyTextColor,
          }}
        >
          {copied ? (
            <CheckIcon sx={{ fontSize: 16 }} />
          ) : (
            <ContentCopyOutlinedIcon sx={{ fontSize: 16 }} />
          )}
        </IconButton>
      </span>
    </Tooltip>
  );
};

export default RichTextCopyButton;
